/**
 * A highlight rolloff for banner photography, as an SVG filter.
 *
 * The problem is not that the banner is bright, it is that the *top* of its
 * range is — a white sky, a white roof — and the page's chrome is white. A
 * black wash fixes that by taking everything down together: the shadows go
 * grey, the picture goes flat, and the whole banner looks like it is behind
 * gauze. Reducing brightness does the same thing by another route.
 *
 * So this is an S-curve, not a dimmer: shadows are pulled DOWN, the midtones
 * are steepened between them, and the highlights are compressed hard at the
 * top. Read the table as a curve from black to white, at inputs 0 / .2 / .4 /
 * .6 / .8 / 1.
 *
 * It used to leave the bottom of the range on the diagonal and only bend the
 * top, which is why the banner could read as flat: nothing was darker than the
 * photograph already was, so squashing the highlights closed the gap between
 * the two ends instead of opening it. After the contrast pass below, a shadow
 * that used to land at 0.20 now lands at 0.08, and white at 0.87 rather than
 * 0.80 — a wider range, with the dark end doing the work.
 *
 * Highlight detail above about 80% input is largely gone. That is the trade:
 * the top of the whites is clipped so the rest of the picture has somewhere to
 * go, and the white chrome over it still has a ceiling to sit against.
 */
const ROLLOFF = '0 0.12 0.33 0.55 0.70 0.80';

export const BANNER_EXPOSURE_ID = 'ap-banner-exposure';

export const BannerExposureDefs = () => (
    <svg
        aria-hidden="true"
        focusable="false"
        className="pointer-events-none absolute size-0"
    >
        <filter
            id={BANNER_EXPOSURE_ID}
            colorInterpolationFilters="sRGB"
            x="0%"
            y="0%"
            width="100%"
            height="100%"
        >
            <feComponentTransfer in="SourceGraphic" result="rolled">
                <feFuncR type="table" tableValues={ROLLOFF} />
                <feFuncG type="table" tableValues={ROLLOFF} />
                <feFuncB type="table" tableValues={ROLLOFF} />
            </feComponentTransfer>
            {/* Compressing the top of the range costs a little separation, so
                a small amount goes back — pivoted about the midtones, which
                the rolloff left where they were. */}
            <feComponentTransfer in="rolled" result="graded">
                <feFuncR type="linear" slope="1.16" intercept="-0.06" />
                <feFuncG type="linear" slope="1.16" intercept="-0.06" />
                <feFuncB type="linear" slope="1.16" intercept="-0.06" />
            </feComponentTransfer>
            {/* Both curves above run per channel, which steepens the distance
                between the channels as well as the distance between light and
                dark — so contrast arrives with colour riding on it, and the
                banner reads oversaturated. The midtone gain is the rolloff's
                own slope through the middle of its table (~1.07) times the
                1.16 above it, so chroma comes back down by the reciprocal of
                that and the photograph keeps its own colour. */}
            <feColorMatrix in="graded" type="saturate" values="0.8" />
        </filter>
    </svg>
);

/**
 * Applied as a style, not a class: the value is `url(#id)`, and the `#` in it
 * does not survive being written as a Tailwind arbitrary property.
 */
export const bannerExposureStyle = {
    filter: `url(#${BANNER_EXPOSURE_ID})`,
} as const;
